import * as React from 'react';
import * as PropTypes from 'prop-types';
import cx from 'classnames';

import { Button, ModalComponent } from '@wkda/styleguide';

import { CookieSelector } from './CookieSelector';
import { PTContent } from './pTypes';
import { useGDPRBannerContext } from './useGDPRBannerContext';
import { SelectionModeButton } from './SelectionModeButton';

import './main.scss';
import * as styles from './GDPRBanner.module.scss';

/**
 * @typedef {Object} GDPRBannerContent
 * @property {string} title
 * @property {string} info
 * @property {string} button
 * @property {{ default: string; selection: string }} secondaryButton
 * @property {string} selectionInfo
 * @property {ContentSection} essential
 * @property {ContentSection} analytical
 * @property {ContentSection} marketing
 */

/**
 *
 * @param {React.MutableRefObject<HTMLFormElement>} formRef
 * @returns
 */
function useCookiesConfigurationMode(formRef) {
  const [isInSelectionMode, setIsSelectionMode] = React.useState(false);
  const { submit, submitAll, isEverythingSelected } = useGDPRBannerContext();

  return {
    isInSelectionMode,

    /**
     * @param {React.FormEvent<HTMLFormElement>} e
     */
    clickHandler: (e) => {
      if (isInSelectionMode) {
        e.preventDefault();

        if (isEverythingSelected) {
          submitAll();
        } else {
          submit().then(() => {
            formRef.current.submit();
          });
        }
      }

      return setIsSelectionMode(true);
    },
  };
}

const getCurrentUrlValue = () =>
  typeof window === 'undefined' ? '' : window.location.href;

const focusTrapOptions = { initialFocus: `.${styles.submitButton}` };

/**
 * @param {Object} props
 * @param {GDPRBannerContent} props.content
 * @param {() => void} [props.onClose]
 * @param {string} [props.className]
 */
export function GDPRBanner({ content, onClose, className }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const formRef = React.useRef(null);
  const { isInSelectionMode, clickHandler } =
    useCookiesConfigurationMode(formRef);
  const { submitAll } = useGDPRBannerContext();

  /**
   * @param {React.FormEvent<HTMLFormElement>} e
   */
  function submitAllHandler(e) {
    e.preventDefault();
    if (!isSubmitted) {
      setIsSubmitted(true);
      submitAll();
    }
  }

  function clickSelectHandler(e) {
    if (isInSelectionMode && !isSubmitted) {
      setIsSubmitted(true);
    }
    if (!isSubmitted) {
      clickHandler(e);
    }
  }

  React.useEffect(() => {
    setIsOpen(true);

    return () => {
      if (typeof onClose === 'function') onClose();
    };
  }, []);

  return (
    <>
      <ModalComponent isOpen={isOpen} focusTrapOptions={focusTrapOptions}>
        <form ref={formRef} action="/v1/gdpr-preferences/" method="POST">
          <div className={cx(styles.banner, className)}>
            <h1 className={styles.title}>{content.title}</h1>
            <p
              className={styles.info}
              dangerouslySetInnerHTML={{
                __html: isInSelectionMode
                  ? content.selectionInfo
                  : content.info,
              }}
            />
            {isInSelectionMode ? (
              <CookieSelector
                content={{
                  essential: content.essential,
                  analytical: content.analytical,
                  marketing: content.marketing,
                }}
              />
            ) : null}
            <Button
              className={cx(styles.submitButton, {
                [styles.submitButtonInSelectionMode]: isInSelectionMode,
              })}
              variant="secondary"
              onClick={submitAllHandler}
              data-qa-selector="gdpr-banner-accept-all-button"
            >
              {content.button}
            </Button>
            <SelectionModeButton
              content={content.secondaryButton}
              onClick={clickSelectHandler}
              isInSelectionMode={isInSelectionMode}
            />

            <input
              type="text"
              defaultValue={getCurrentUrlValue()}
              name="currentUrl"
              className={styles.hidden}
            />
          </div>
        </form>
      </ModalComponent>
    </>
  );
}

GDPRBanner.propTypes = {
  content: PTContent.isRequired,
  onClose: PropTypes.func,
  className: PropTypes.string,
};
